// A device's front, drawn from its ports (#292). One shape per named port,
// laid out in two rows the way a switch's faceplate is, with whatever is
// known about each port painted on it as a layer: what the intent says today;
// what the port is doing, once #295 reads it; whether an apply would change
// it, once #294 computes that. The layers are props, so the same component
// serves the device page, the port editor, the topology and the planner, and
// a layer nobody has data for is simply absent rather than guessed.
//
// **Ports are named, never numbered.** A switch calls them Ethernet0…27 and an
// access point calls them LAN or LAN1…4 (ADR-0052 decided what an access
// point's mean, on #264); the drawing shows the short form and the button carries the full
// name, and nothing here derives a count from a model string — the list of
// ports *is* the input. Geometry is optional: with `row` and `col` on each
// port (the catalogue's authored layout, #290/#310) the drawing honours them; with
// none, ports pair up in declared order, first port top-left, the way every
// switch in the corpus is numbered.
//
// **Every port is a button.** That is what makes each one reachable from the
// keyboard and named to a screen reader (#204), and it is why the component
// takes `onSelect` rather than a tooltip: a port an operator can focus is a
// port they can ask about. The chassis behind the buttons is one SVG that
// carries no information of its own.
//
// The chassis is painted in fixed colours in both themes, like the RF and
// status palettes (theme.css): a faceplate is a picture of hardware, and a
// picture that changed with the theme would make the same port look like a
// different state on two screens.
//
// **A picture is a layer, never the drawing** (#298). When the model the
// device resolved to carries a picture with a box per port, the same buttons
// are placed on those boxes over the picture, in its own coordinates scaled
// to fit; a port the picture has no box for is drawn in vector over the
// picture, in a strip along its foot, so nothing is dropped for want of a
// box and nothing depends on the image. The strip keeps the layout's rows
// and columns, so an access point's LAN1–LAN4 sit in the one row its plate
// has them in (#567). On a box, a state is a translucent tint over the
// drawing's own port — the picture is the hardware and the state is painted
// on it, never a square over it — and a port with nothing to say shows the
// drawing alone; the legend's words are the same on a box and on a vector
// cell. There is no toggle to a vector view of a model that has a picture:
// every model's picture is a drawing of it (#554), and the vector faceplate
// stands in only where there is none.
//
// **The drawing is sized to be read, not to a constant** (#328). `place()`
// lays ports out in one logical unit — the geometry the topology (#297)
// positions its faceplates and draws its lines from, which this never
// changes — and a face asked to `fit` scales that geometry uniformly to the
// width its container gives it, between a floor and a ceiling set on the
// label: never smaller than a port number can be read at, never larger than
// makes it clearer. A face that does not `fit` draws at scale 1, as before.
// The photo is drawn at the container's width too, never past its own pixels,
// never taller than `FACE_TALL` when it is taller than it is wide (#1281),
// and never so small that a box is smaller than a vector port; what the
// container cannot hold, `.face-wrap` scrolls, never the page.
//
// **Several ports are picked with the mouse the way files are** (#467). A
// caller that hands the face a `selection` — the ports held, and what to do
// with the next set — gets a click that replaces, Shift that adds, Ctrl or ⌘
// that toggles, a rectangle dragged across the face that takes every port it
// touches under the same three modifiers, a click on the chassis that clears,
// and Escape that clears. The rectangle is hit-tested against each button's
// own box on screen, so the fitted drawing's transform and the photo's scale
// cost it nothing, and it is the mouse's alone: a finger on the face keeps
// scrolling `.face-wrap`. The single `selected`/`onSelect` contract is
// untouched for every caller that does not hand a `selection` in, and
// nothing here acts on the set — what consumes it is later work.
import { useLayoutEffect, useRef, useState, type CSSProperties, type HTMLAttributes, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode, type RefObject } from "react";

/** A port's cage, as the catalogue's layout names it: an RJ-45 jack, an SFP
 * cage, or a QSFP cage, which is wider (#1756). */
export type PortKind = "copper" | "sfp" | "qsfp";

/** A layout's port kind as the faceplate draws it: one it knows, or none, which draws the
 * default shape. A model's layout kind is the list or any other string (ADR-0511). */
export function portKindOf(kind: string | undefined): PortKind | undefined {
  return kind === "copper" || kind === "sfp" || kind === "qsfp" ? kind : undefined;
}

/** A product photo and where each port is in it, in its own pixels
 * (`/v1/device-models/{model}` `image`); `src` is the URL the API serves the
 * bytes at. */
export interface FacePhoto {
  src: string;
  width: number;
  height: number;
  boxes: { port: string; x: number; y: number; w: number; h: number }[];
  /** A picture cropped to part of its width (#1494): the topology draws a
   * desk unit's body without the rack frame's filler panels. `x` is where the
   * crop starts in the source's pixels and `width` the source's whole width;
   * the photo's own `width` and boxes are the crop's. */
  crop?: { x: number; width: number };
}

/** One port, as the device names it. `row`/`col` place it when known. */
export interface FacePort { name: string; kind?: PortKind; row?: number; col?: number }

/** What is painted on a port. Every field is optional and independent, so a
 * caller with intent and no live state paints intent alone.
 *
 * `intent`: `described` when the site holds intent for the port, `disabled`
 * when that intent turns it off. A port with neither is one Zenith does not
 * describe — the API's own words: "Zenith declining to describe a port is not
 * Zenith turning it off" — and it is drawn as such, never as off. `unread` is
 * a caller that did not read the intent at all (the topology, #297) and so
 * says nothing about it, rather than "no planned settings". */
export interface PortState {
  intent?: "described" | "disabled" | "unread";
  /** The one word for what the port is doing, once a read supplies it (#295). */
  link?: "up" | "down";
  /** Delivering power right now — a live fact, from the gateway. */
  poe?: boolean;
  /** Can deliver power — a fact about the hardware, from the catalogue's
   * measured `ports[].poe` (ADR-0036 decision 1), so a catalogue card can
   * show which ports carry PoE without a device (#362). A different mark
   * from `poe`, because "can" painted as "is" would lie on the device page. */
  poeCapable?: boolean;
  /** The port the switch reports it reaches its controller through (§11.3). */
  uplink?: boolean;
  /** An apply would change this port (#294). */
  change?: boolean;
  /** A declared link ends here (#297): the other end, in words. Intent, drawn
   * as a mark on the port; what the switch observes is another layer. */
  cabled?: string;
  /** What the button's tooltip says beyond the port's name. */
  detail?: string;
}

/** Multi-selection (#467): the ports held, in the face's own order, and the
 * set the next act produces. Given, it takes the place of `selected` and
 * `onSelect`; the face reads neither. */
export interface FaceSelection { ports: readonly string[]; onChange(ports: string[]): void }

/** A card the caller draws for the port under the pointer or the focus
 * (#876), in place of the browser's `title` tooltip: given, a port reports
 * when it is hovered or focused and when it is left, carries no `title`, and
 * the port whose card is open is described by the card's `id`. */
export interface FacePeek { id: string; current?: string; onPeek(name: string | undefined): void }

export type SelectMode = "replace" | "add" | "toggle";

/** Which act the modifiers name. ⌘ stands with Ctrl because on macOS
 * Ctrl+click is the context menu; Ctrl or ⌘ wins over Shift when both are
 * held, since toggling is the more deliberate of the two. */
export function modeOf(e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }): SelectMode {
  return e.ctrlKey || e.metaKey ? "toggle" : e.shiftKey ? "add" : "replace";
}

/** The set after `hit` is applied to `current` in `mode`: replaced by it,
 * widened by it, or each hit port flipped. Answered in `order`, the face's
 * port list, so the set reads as the drawing does whatever order the ports
 * were picked in. */
export function applySelection(current: readonly string[], hit: readonly string[], mode: SelectMode, order: readonly string[]): string[] {
  const held = new Set(mode === "replace" ? [] : current);
  for (const h of hit) {
    if (mode === "toggle" && held.has(h)) held.delete(h);
    else held.add(h);
  }
  return order.filter((n) => held.has(n));
}

/** How far the mouse travels before a press is a rectangle and not a click. */
const DRAG_PX = 4;

const CELL = 24, SFP = 30, QSFP = 38, GAP = 6, PAD = 10, LABEL = 11;
/** A port button's height; exported with `place` so a drawing laid over the
 * faceplate (the topology's lines, #297) meets each port where it is. */
export const H = 18;

/** The label on a vector port at scale 1, and the bounds a fitted drawing
 * holds it between (#328). Below 12 px a port number is not read at arm's
 * length — the 8 px this drew at is what the item was filed about — and above
 * 16 px the drawing is larger without being clearer; the milestone's canvas
 * draws the ECS-4150-28P at 16.5. The scale is uniform, so the cell, the PoE
 * dot and every mark follow the label. */
export const LABEL_PX = 8;
const LABEL_MIN = 12, LABEL_MAX = 16;
export const MIN_SCALE = LABEL_MIN / LABEL_PX, MAX_SCALE = LABEL_MAX / LABEL_PX;

/** The uniform scale a drawing `width` units wide takes to fit `available`
 * px, held between the label bounds; unmeasured, it takes the floor, so the
 * first paint is the smallest a fitted face is ever drawn and the measured
 * one only grows it. */
export function fitScale(width: number, available: number | undefined): number {
  if (available === undefined) return MIN_SCALE;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, available / width));
}

/** The scale a photo is drawn at to fit `available` px: never past its own
 * pixels, which would blur it, and never so small that a box is drawn
 * smaller than a vector port is (`H`) — below that a box is neither read nor
 * pressed, and the container scrolls instead. A photo with no box has no
 * floor: every port is then in the strip, and the image is only a picture.
 * Unmeasured, it takes the floor, as the drawing does. */
export function photoScale(photo: FacePhoto, available: number | undefined): number {
  const smallest = Math.min(...photo.boxes.map((b) => Math.min(b.w, b.h)));
  const floor = Number.isFinite(smallest) && smallest > 0 ? Math.min(1, H / smallest) : 0;
  if (available === undefined) return floor || 1;
  return Math.min(1, Math.max(floor, available / photo.width));
}

/** How tall a fitted portrait picture may be drawn, in px: an access point's
 * plate read at a glance, with what is around it still on screen. */
export const FACE_TALL = 420;

/** The scale a fitted face draws its photo at (#1281): `photoScale` for the
 * width the container gives it, and a picture taller than it is wide — an
 * access point's plate, which at a card's or a page's width is a poster a
 * screen tall — held to `FACE_TALL` px by narrowing that width to what the
 * bound allows. A landscape picture keeps the container's width. The floor
 * still wins, so a box stays pressable; unmeasured, a portrait picture takes
 * the bound rather than its own pixels, so the first paint is never the
 * poster. The one implementation for every fitted face — the device page's
 * head, the catalogue card, a cabling end, a planned device's ports; the
 * topology draws at its own scale with `fit` off and does not come here. */
export function fitPhotoScale(photo: FacePhoto, available: number | undefined): number {
  if (photo.height <= photo.width) return photoScale(photo, available);
  const tall = FACE_TALL * photo.width / photo.height;
  return photoScale(photo, Math.min(available ?? tall, tall));
}

/** Where each port sits, in pixels, from its own geometry or from pairing.
 * Pure: the topology places its faceplates and its lines from this alone,
 * with no measurement of the page. */
export function place(ports: FacePort[]) {
  const placed = ports.map((p, i) => ({
    ...p,
    row: p.row ?? i % 2,
    col: p.col ?? Math.floor(i / 2),
    w: p.kind === "qsfp" ? QSFP : p.kind === "sfp" ? SFP : CELL,
  }));
  // A column is as wide as its widest port, so an SFP column does not overlap
  // the copper one beside it.
  const widths: number[] = [];
  for (const p of placed) widths[p.col] = Math.max(widths[p.col] ?? 0, p.w);
  const x: number[] = [];
  let acc = PAD;
  widths.forEach((w, c) => { x[c] = acc; acc += (w ?? CELL) + GAP; });
  const rows = Math.max(1, ...placed.map((p) => p.row + 1));
  const width = Math.max(acc - GAP + PAD, PAD * 2 + CELL);
  const height = PAD * 2 + LABEL * 2 + rows * H + (rows - 1) * GAP;
  return {
    width, height,
    ports: placed.map((p) => ({ ...p, x: x[p.col] ?? PAD, y: PAD + LABEL + p.row * (H + GAP) })),
  };
}

/** Ethernet2 before Ethernet10: the number decides where the names share a
 * prefix, and the string does otherwise. */
export function byNumber(a: string, b: string): number {
  const na = /(\d+)$/.exec(a), nb = /(\d+)$/.exec(b);
  if (na && nb && a.slice(0, -na[1]!.length) === b.slice(0, -nb[1]!.length)) return Number(na[1]) - Number(nb[1]);
  return a.localeCompare(b);
}

/** The number the switch itself prints for a port Zenith names `EthernetN`:
 * N + 1 (#515).
 *
 * **One rule, stated here and nowhere else in the page.** `EthernetN` counts
 * from 0, and the switch's front panel and its CLI count from 1 — the staging
 * ECS4150-28P calls `Ethernet16` port 17, `Eth1/17`
 * (docs/evidence/2026-09-18-a-switch-creates-the-vlan-interface-zenith-sends-it-layer-2-only.md),
 * and the catalogue's layouts are authored on the same rule
 * (internal/catalog/models/README.md, "`Ethernet0` is port 1"). A faceplate
 * that drew the name's own digits sent an operator to the neighbouring port.
 *
 * It is a display label and nothing else. Intent, the API and the
 * configuration all say `EthernetN` (§11.3), and a name the switch reports is
 * resolved through its configuration, never by this. `internal/catalog`'s
 * test holds the rule against every catalogued switch model: each one's ports
 * are `Ethernet0` upward with no gap, so N + 1 is 1 to the port count. Only
 * the ECS4150-28P has been read against a unit; the other five rest on the
 * datasheets the README cites.
 *
 * Undefined for anything else: an access point's `LAN1` is already the number
 * on its case. */
export function vendorNumber(name: string): number | undefined {
  const m = /^Ethernet(\d+)$/.exec(name);
  return m ? Number(m[1]) + 1 : undefined;
}

/** A port as the drawing names it (#1480): the number printed on the
 * switch, `port 1` for `Ethernet0`, because the topology is the physical
 * view. A name with no printed number (an access point's `LAN1`, a name
 * that is not `EthernetN`) is itself. */
export function physicalPort(name: string): string {
  const n = vendorNumber(name);
  return n === undefined ? name : `port ${n}`;
}

/** A switch port's name as Zenith says it, with the number printed on the
 * switch beside it, so neither is read as the other. */
export function PortName({ name }: { name: string }) {
  const n = vendorNumber(name);
  return <><code>{name}</code>{n !== undefined && <> <span className="muted">· port {n}</span></>}</>;
}

/** The short form drawn on the port: the number the switch prints for an
 * EthernetN (vendorNumber), the tail of a LANn, the name itself when it is
 * already short. */
export function shortName(name: string): string {
  const n = vendorNumber(name);
  if (n !== undefined) return String(n);
  const m = /^(?:LAN|eth|lan)(\d+)$/i.exec(name);
  if (m) return m[1]!;
  return name.length <= 4 ? name : name.slice(0, 4);
}

/** The width a fitted face has to fill: its own block's, read before paint
 * and again whenever it changes. `undefined` until measured, and always
 * when the face does not fit — a caller that placed the face itself (the
 * topology) is never measured. */
function useFitWidth(fit: boolean): [RefObject<HTMLDivElement | null>, number | undefined] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>();
  useLayoutEffect(() => {
    const el = ref.current;
    if (!fit || !el) return;
    const read = () => setWidth(el.clientWidth || undefined);
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);
  return [ref, width];
}

export default function DeviceFace({ ports, states = {}, selected, onSelect, selection, peek, far, label, photo, fit = false, scale: given }: {
  ports: FacePort[];
  states?: Record<string, PortState>;
  /** The port whose button is pressed. */
  selected?: string;
  onSelect?(name: string): void;
  /** Several ports at once (#467); given, `selected` and `onSelect` are not read. */
  selection?: FaceSelection;
  /** The caller's port card (#876); given, it replaces the `title` tooltip. */
  peek?: FacePeek;
  /** The ports at the far end of a lit cable (#1493), marked for the eye. */
  far?: string[];
  /** What the drawing is, for a screen reader: "SW1-Cigales, 28 ports". */
  label: string;
  /** The model's picture, when it has one; the vector drawing stands in otherwise. */
  photo?: FacePhoto;
  /** Size the drawing to the width its container gives it (#328). Off, the
   * face draws at scale 1 in `place()`'s own pixels, which is what a caller
   * that lays other things over it (the topology) relies on. */
  fit?: boolean;
  /** A picture's scale, the caller's own, with `fit` off (#586): the topology
   * draws every picture at one scale it decides from the drawing, so the
   * site's switches keep the physical relation their pictures share (#567),
   * and its lines meet each box where `photoGeometry` says it is. A vector
   * face ignores it and draws at 1, as it always has in the topology. */
  scale?: number;
}) {
  const [ref, available] = useFitWidth(fit);
  const scale = !fit ? (photo && given ? given : 1) : photo ? fitPhotoScale(photo, available) : fitScale(place(ports).width, available);
  // One pair for both contracts: which buttons are pressed, and what a press
  // does. The multi-selection one applies the modifiers; the single one is
  // the caller's callback as it has always been.
  const order = ports.map((p) => p.name);
  const held = selection ? new Set(selection.ports) : undefined;
  const press: Press = {
    pressed: (name) => (held ? held.has(name) : selected === name),
    press: (name, e) => { if (selection) selection.onChange(applySelection(selection.ports, [name], modeOf(e), order)); else onSelect?.(name); },
    peek,
    far: (name) => !!far?.includes(name),
  };
  const buttons = photo
    ? <PhotoFace ports={ports} states={states} press={press} label={label} photo={photo} scale={scale} />
    : <VectorFace ports={ports} states={states} press={press} label={label} scale={scale} />;
  const face = selection ? <SelectArea selection={selection} order={order}>{buttons}</SelectArea> : buttons;
  if (!fit) return face;
  // The floor held and the drawing is wider than its box (#362): the class
  // is what lets `.face-wrap` show that there is more to the right, because
  // a 54-port face that stops at port 49 with nothing said reads as a
  // 50-port face. Unknown until the box has been measured.
  const drawn = (photo ? photo.width : place(ports).width) * scale;
  const overflowing = available !== undefined && drawn > available;
  return <div className={"face-fit" + (overflowing ? " is-overflowing" : "")} ref={ref}>{face}</div>;
}

/** A box `width` × `height` logical units drawn `scale` times larger: the
 * outer element takes the scaled size, so the page lays out around it and
 * `.face-wrap` scrolls it, and the inner one carries the geometry at scale 1
 * under one transform, so every pixel value stays the one `place()` gave. */
function Scaled({ width, height, scale, className, style, children, ...rest }: {
  width: number; height: number; scale: number; className: string; style?: CSSProperties; children: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, "className" | "children" | "style">) {
  return (
    <div className={className} style={{ ...style, width: width * scale, height: height * scale }} {...rest}>
      <div className="face-scaled" style={{ width, height, transform: scale === 1 ? undefined : `scale(${scale})` }}>
        {children}
      </div>
    </div>
  );
}

/** Which port buttons are pressed and what pressing one does — the one
 * shape both contracts are folded into before the buttons are drawn. */
interface Press { pressed(name: string): boolean; press(name: string, e: MouseEvent<HTMLButtonElement>): void; peek?: FacePeek; far?(name: string): boolean }

/** The vector drawing: an SVG chassis and a button per port. */
function VectorFace({ ports, states, press, label, scale }: {
  ports: FacePort[]; states: Record<string, PortState>; press: Press; label: string;
  scale: number;
}) {
  const g = place(ports);
  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => { const s = arrowStep(e); if (s) stepFocus(e, s); };
  return (
    <Scaled className="face" role="group" aria-label={label} width={g.width} height={g.height} scale={scale}>
      <svg className="face-chassis" width={g.width} height={g.height} viewBox={`0 0 ${g.width} ${g.height}`} aria-hidden="true">
        <rect x="0.5" y="0.5" width={g.width - 1} height={g.height - 1} rx="6" />
        <rect className="face-bezel" x="4" y="4" width={g.width - 8} height={g.height - 8} rx="4" />
      </svg>
      {g.ports.map((p) => portButton(p, { x: p.x, y: p.y, w: p.w, h: H, label: LABEL_PX }, states[p.name] ?? {}, press, onKey))}
    </Scaled>
  );
}

/** One port's button, wherever it is placed: the same paint and the same
 * words on a vector cell and on a photo's box. `label` is the font size the
 * short name is drawn at, in the button's own pixels. */
function portButton(p: FacePort, at: { x: number; y: number; w: number; h: number; label: number }, s: PortState, press: Press,
  onKey: (e: KeyboardEvent<HTMLButtonElement>) => void, extra = "") {
  const pressed = press.pressed(p.name);
  const cls = ["face-port", extra,
    p.kind === "sfp" ? "face-port-sfp" : p.kind === "qsfp" ? "face-port-qsfp" : "",
    s.intent === "described" ? "is-described" : s.intent === "disabled" ? "is-disabled" : "",
    s.link === "up" ? "is-up" : s.link === "down" ? "is-down" : "",
    s.uplink ? "is-uplink" : "", s.change ? "is-change" : "", s.cabled ? "is-cabled" : "", s.poeCapable ? "is-poe-capable" : "",
    pressed ? "is-selected" : "", press.far?.(p.name) ? "is-far" : "",
    // The port whose card is open (#1496), marked as its far end is (#1493),
    // so the card, this port and the far port read as one thing.
    press.peek?.current === p.name ? "is-near" : ""].filter(Boolean).join(" ");
  const words = [
    p.name,
    // The kind is a fact the bar alone cannot carry to a screen reader (#374).
    p.kind === "sfp" ? "SFP" : p.kind === "qsfp" ? "QSFP" : "",
    s.uplink ? "uplink" : "",
    s.intent === "disabled" ? "disabled in planned settings" : s.intent === "described" ? "has planned settings" : s.intent === "unread" ? "" : "no planned settings",
    s.poeCapable ? "PoE-capable" : "",
    s.link ? `link ${s.link}` : "", s.poe ? "delivering power" : "", s.change ? "applying the plan would change it" : "",
    s.cabled ? `declared link to ${s.cabled}` : "",
    s.detail ?? "",
  ].filter(Boolean);
  // With a card (#876) the port says when it is looked at and when it is
  // left, and the browser's tooltip is not drawn over the card.
  const peek = press.peek;
  const looking = peek ? {
    onMouseEnter: () => peek.onPeek(p.name), onFocus: () => peek.onPeek(p.name),
    onMouseLeave: () => peek.onPeek(undefined), onBlur: () => peek.onPeek(undefined),
    "aria-describedby": peek.current === p.name ? peek.id : undefined,
  } : { title: words.join(" · ") };
  return (
    <button key={p.name} type="button" className={cls} style={{ left: at.x, top: at.y, width: at.w, height: at.h, fontSize: at.label }}
      aria-label={words.join(", ")} aria-pressed={pressed} data-port={p.name} {...looking}
      onClick={(e) => press.press(p.name, e)} onKeyDown={onKey}>
      <span aria-hidden="true">{shortName(p.name)}</span>
      {s.poeCapable && <i className="face-poe-capable" aria-hidden="true" />}
      {s.poe && <i className="face-poe" aria-hidden="true" />}
    </button>
  );
}

/** Arrow keys move between the buttons of one face in document order and
 * wrap, so a keyboard user can walk a 54-port switch without 54 tabs. */
function stepFocus(e: KeyboardEvent<HTMLButtonElement>, step: number) {
  e.preventDefault();
  const all = [...(e.currentTarget.closest(".face")?.querySelectorAll<HTMLButtonElement>("button.face-port") ?? [])];
  const i = all.indexOf(e.currentTarget);
  all[(i + step + all.length) % all.length]?.focus();
}

function arrowStep(e: KeyboardEvent<HTMLButtonElement>): number {
  return e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
}

/** A box's label follows the box: a third of its drawn height, rounded up,
 * never below the 7 px it was fixed at, never above the vector ceiling. */
function boxLabel(h: number): number {
  return Math.min(LABEL_MAX, Math.max(7, Math.ceil(h / 3)));
}

/** The ports a strip draws, with the layout's rows and columns kept and
 * closed up (#567): an access point's `LAN1`…`LAN4` are authored in one row
 * and were being paired into two by `place()`'s default. A column or row the
 * strip's ports do not use is dropped so a strip holding the last two ports
 * of a 28-port layout is two columns wide, not fourteen; a port with no
 * geometry keeps none and pairs as before. */
export function stripPorts(ports: FacePort[]): FacePort[] {
  const rows = [...new Set(ports.map((p) => p.row).filter((r): r is number => r !== undefined))].sort((a, b) => a - b);
  const cols = [...new Set(ports.map((p) => p.col).filter((c): c is number => c !== undefined))].sort((a, b) => a - b);
  return ports.map((p) => ({
    name: p.name, kind: p.kind,
    ...(p.row !== undefined ? { row: rows.indexOf(p.row) } : {}),
    ...(p.col !== undefined ? { col: cols.indexOf(p.col) } : {}),
  }));
}

/** Where a photo face puts everything, in the face's own drawn pixels at
 * `scale`: the picture's size, the strip under it for the ports the photo
 * shows no box for, and each port's box on screen — a photo box scaled, or
 * a strip cell placed by `place()` and scaled with the strip. `PhotoFace`
 * draws from this and the topology (#586) anchors its lines on it, so a
 * line meets a box where the button is and not where a vector cell would
 * have been. Pure: nothing is measured from the page. */
export function photoGeometry(ports: FacePort[], photo: FacePhoto, scale: number) {
  const width = Math.round(photo.width * scale), imageHeight = Math.round(photo.height * scale);
  const box = new Map(photo.boxes.map((b) => [b.port, b]));
  const boxed = ports.filter((p) => box.has(p.name));
  const unboxed = ports.filter((p) => !box.has(p.name));
  // The vector strip for unboxed ports, in the layout's own rows, fitted to
  // the picture's drawn width so it reads as an inset over it.
  const strip = unboxed.length ? place(stripPorts(unboxed)) : undefined;
  const stripScale = strip ? fitScale(strip.width, width) : 1;
  const height = imageHeight + (strip ? strip.height * stripScale : 0);
  const at = new Map<string, { x: number; y: number; w: number; h: number }>();
  for (const p of boxed) {
    const b = box.get(p.name)!;
    at.set(p.name, { x: Math.round(b.x * scale), y: Math.round(b.y * scale), w: Math.max(6, Math.round(b.w * scale)), h: Math.max(6, Math.round(b.h * scale)) });
  }
  for (const p of strip?.ports ?? []) at.set(p.name, { x: p.x * stripScale, y: imageHeight + p.y * stripScale, w: p.w * stripScale, h: H * stripScale });
  return { width, height, imageHeight, boxed, unboxed, strip, stripScale, at };
}

/** The picture view: the image under, a button on every box, and the ports
 * with no box drawn in vector in a strip over the picture's foot. */
function PhotoFace({ ports, states, press, label, photo, scale }: {
  ports: FacePort[]; states: Record<string, PortState>; press: Press; label: string;
  photo: FacePhoto; scale: number;
}) {
  const { width, height, imageHeight, boxed, unboxed, strip: g, stripScale, at } = photoGeometry(ports, photo, scale);
  const onKey = (e: KeyboardEvent<HTMLButtonElement>) => { const s = arrowStep(e); if (s) stepFocus(e, s); };
  return (
    <div className="face face-photo" role="group" aria-label={label} style={{ width, height }}>
      {photo.crop
        ? <div className="face-image-crop" style={{ width, height: imageHeight }}><img className="face-image" src={photo.src} alt="" width={Math.round(photo.crop.width * scale)} height={imageHeight} style={{ left: -Math.round(photo.crop.x * scale) }} draggable={false} /></div>
        : <img className="face-image" src={photo.src} alt="" width={width} height={imageHeight} draggable={false} />}
      {boxed.map((p) => {
        const b = at.get(p.name)!;
        return portButton(p, { ...b, label: boxLabel(b.h) }, states[p.name] ?? {}, press, onKey, "face-port-box");
      })}
      {g && (
        <Scaled className="face-strip" role="group" aria-label={`${unboxed.length} ${unboxed.length === 1 ? "port" : "ports"} the photo does not show`}
          width={g.width} height={g.height} scale={stripScale} style={{ top: imageHeight }}>
          <svg className="face-chassis" width={g.width} height={g.height} viewBox={`0 0 ${g.width} ${g.height}`} aria-hidden="true">
            <rect x="0.5" y="0.5" width={g.width - 1} height={g.height - 1} rx="6" />
          </svg>
          {g.ports.map((p) => portButton(p, { x: p.x, y: p.y, w: p.w, h: H, label: LABEL_PX }, states[p.name] ?? {}, press, onKey))}
        </Scaled>
      )}
    </div>
  );
}

/** The mouse's rectangle, the chassis click and Escape (#467), around a
 * face whose buttons already answer a click. A press that travels `DRAG_PX`
 * becomes a rectangle: it is drawn in this element's own coordinates, and on
 * release every `.face-port` whose box on screen it touches is the hit —
 * read from the DOM, so the fitted drawing's transform and the photo's scale
 * are honoured by the browser rather than recomputed here. The click the
 * browser fires after that release is swallowed, or a rectangle that ended
 * on a port would also press it. Only the mouse draws one: a finger's
 * pointer is left to `.face-wrap`, which scrolls. */
function SelectArea({ selection, order, children }: { selection: FaceSelection; order: string[]; children: ReactNode }) {
  // The press in flight — where it started, whether it has become a
  // rectangle — and whether the click that follows a rectangle is still owed.
  const drag = useRef<{ x0: number; y0: number; rect: boolean } | undefined>(undefined);
  const swallow = useRef(false);
  const [box, setBox] = useState<{ x: number; y: number; w: number; h: number }>();
  const pick = (hit: string[], e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }) =>
    selection.onChange(applySelection(selection.ports, hit, modeOf(e), order));
  const inFace = (t: EventTarget | null) => t instanceof Element && t.closest(".face") !== null;

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    swallow.current = false;
    if (e.pointerType !== "mouse" || e.button !== 0 || !inFace(e.target)) return;
    drag.current = { x0: e.clientX, y0: e.clientY, rect: false };
    // On the chassis or the photo, the browser's own press would start
    // selecting the page's text; on a button it would move focus, which stays.
    if (!(e.target as Element).closest("button")) e.preventDefault();
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    if (!d.rect) {
      if (Math.abs(e.clientX - d.x0) < DRAG_PX && Math.abs(e.clientY - d.y0) < DRAG_PX) return;
      d.rect = true;
      // jsdom has no pointer capture; a browser keeps the moves coming when
      // the mouse leaves the face mid-drag.
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    const r = e.currentTarget.getBoundingClientRect();
    setBox({ x: Math.min(d.x0, e.clientX) - r.left, y: Math.min(d.y0, e.clientY) - r.top, w: Math.abs(e.clientX - d.x0), h: Math.abs(e.clientY - d.y0) });
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    drag.current = undefined;
    if (!d?.rect) return;
    setBox(undefined);
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    const area = { left: Math.min(d.x0, e.clientX), right: Math.max(d.x0, e.clientX), top: Math.min(d.y0, e.clientY), bottom: Math.max(d.y0, e.clientY) };
    const hit = [...e.currentTarget.querySelectorAll<HTMLButtonElement>("button.face-port")]
      .filter((b) => { const r = b.getBoundingClientRect(); return r.left < area.right && r.right > area.left && r.top < area.bottom && r.bottom > area.top; })
      .map((b) => b.dataset.port!)
      .filter((n) => order.includes(n));
    pick(hit, e);
    swallow.current = true;
  };
  const onPointerCancel = () => { drag.current = undefined; setBox(undefined); };
  // The click after a rectangle is not a press; the next one is, and a
  // rectangle that ended off the page owes none, which the next press resets.
  const onClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    if (!swallow.current) return;
    swallow.current = false;
    e.stopPropagation();
    e.preventDefault();
  };
  // A click on the chassis or the photo, with no port under it, clears —
  // with no modifier held; Shift or Ctrl/⌘ on nothing is nothing.
  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!inFace(e.target) || (e.target as Element).closest("button.face-port")) return;
    if (modeOf(e) === "replace" && selection.ports.length > 0) pick([], e);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Escape" || selection.ports.length === 0) return;
    e.stopPropagation();
    selection.onChange([]);
  };
  return (
    // Presentation: the buttons inside are what the keyboard and the reader
    // reach; this element is the mouse's and says nothing of its own.
    <div className={"face-select" + (box ? " is-dragging" : "")} role="presentation"
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}
      onClickCapture={onClickCapture} onClick={onClick} onKeyDown={onKeyDown}>
      {children}
      {box && <div className="face-marquee" aria-hidden="true" style={{ left: box.x, top: box.y, width: box.w, height: box.h }} />}
    </div>
  );
}

/** What the drawing's marks mean, said in words beside it (ZENITH.md §18:
 * state never travels by colour alone). Only the marks the caller can paint
 * are listed, so a legend never promises a layer the page has no data for. */
export function FaceLegend({ live = false, change = false, links = false, uplink = true, intent = true }: { live?: boolean; change?: boolean; links?: boolean;
  /** Off on a planned switch (#521): nothing can say which port is its uplink until a bound switch does. */
  uplink?: boolean;
  /** Off on an access point (#589): nothing describes its ports, so a key for intent would promise a layer it never carries. */
  intent?: boolean;
}) {
  return (
    <ul className="face-legend" aria-label="Legend">
      {intent && <li><i className="face-key is-described" aria-hidden="true" />has planned settings</li>}
      {intent && <li><i className="face-key" aria-hidden="true" />no planned settings</li>}
      {intent && <li><i className="face-key is-disabled" aria-hidden="true" />disabled in planned settings</li>}
      {uplink && <li><i className="face-key is-uplink" aria-hidden="true" />uplink · protected by the plan</li>}
      {change && <li><i className="face-key is-change" aria-hidden="true" />the plan would change it</li>}
      {live && <li><i className="face-key is-up" aria-hidden="true" />link up</li>}
      {links && <li><i className="face-key is-cabled" aria-hidden="true" />a declared link ends here</li>}
    </ul>
  );
}
